import { useState } from "react";
import { unlockAudio } from "../hooks/useDevice";
import { useCommand } from "../hooks/useCommand";
import { howToPlayPath } from "../lib/scripts";
import { APP_VERSION } from "../lib/version";
import { useSession } from "../net/SessionContext";
import type { PlayerView } from "../protocol";
import { isHost } from "../state/selectors";
import { useGame } from "../state/store";
import { Sheet } from "./Sheet";

export function MenuSheet({
  open,
  onClose,
  onCharacters,
  view,
}: {
  open: boolean;
  onClose: () => void;
  onCharacters: () => void;
  view: PlayerView;
}) {
  const prefs = useGame((s) => s.prefs);
  const setPrefs = useGame((s) => s.setPrefs);
  const session = useSession();
  const { run, busy } = useCommand();
  const host = isHost(view);
  const [target, setTarget] = useState("");
  const [issued, setIssued] = useState<{ name: string; code: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const issue = async () => {
    const seat = view.seats.find((s) => s.seat_id === target);
    if (!seat) return;
    setError(null);
    const result = await session.issueReseatCode(seat.seat_id);
    if (result.kind === "ack" && result.result?.reseat_code) {
      setIssued({ name: seat.name, code: result.result.reseat_code });
    } else if (result.kind === "error") {
      setError(result.message);
    }
  };

  const playing = view.phase !== "lobby" && view.phase !== "game_over";
  const endGame = async () => {
    if (!window.confirm("End this game for everyone? Nobody wins, and every role is shown."))
      return;
    const result = await run({ type: "end_game" });
    if (result.kind === "ack") onClose();
  };

  const leave = async () => {
    if (!window.confirm("Leave this room?")) return;
    await run({ type: "leave_room" });
  };

  return (
    <Sheet open={open} onClose={onClose} label="Menu">
      <div className="row-between">
        <h3>Menu</h3>
        <button className="icon-btn" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="stack-sm">
        <span className="eyebrow">This phone</span>
        <label className="row-between card card-tight">
          <span>
            Vibrate
            <br />
            <span className="small muted">
              {view.config.night_decoys
                ? "When your phone needs you, and when you wake at night."
                : "When your phone needs you. Never at night."}
            </span>
          </span>
          <input
            type="checkbox"
            checked={prefs.haptics}
            onChange={(e) => setPrefs({ haptics: e.target.checked })}
          />
        </label>
        <label className="row-between card card-tight">
          <span>
            Sound
            <br />
            <span className="small muted">
              A chime at the same moments.{!view.config.night_decoys && " Never at night."}
            </span>
          </span>
          <input
            type="checkbox"
            checked={prefs.sound}
            onChange={(e) => {
              if (e.target.checked) unlockAudio();
              setPrefs({ sound: e.target.checked });
            }}
          />
        </label>
      </div>

      <div className="stack-sm">
        <button className="btn btn-ghost" onClick={onCharacters}>
          Characters
        </button>
        {/* a new tab: leaving this page would drop the seat's connection */}
        <a
          className="btn btn-ghost"
          href={howToPlayPath(view.config.script_id)}
          target="_blank"
          rel="noopener"
        >
          How to play
        </a>
        <span className="tiny faint center">Opens in a new tab, so you stay connected here.</span>
      </div>

      {host && (
        <div className="stack-sm">
          <span className="eyebrow">Host: rejoin code</span>
          <p className="small muted">
            If someone&apos;s phone dies, give them a code to take their seat back on another
            device. It works once, for 10 minutes.
          </p>
          <div className="row">
            <select
              className="input"
              value={target}
              onChange={(e) => {
                setTarget(e.target.value);
                setIssued(null);
              }}
              aria-label="Player"
            >
              <option value="">Choose a player…</option>
              {view.seats
                .filter((s) => s.seat_id !== view.me_seat_id)
                .map((s) => (
                  <option key={s.seat_id} value={s.seat_id}>
                    {s.name}
                    {s.connected ? "" : " (offline)"}
                  </option>
                ))}
            </select>
            <button className="btn btn-small" disabled={!target} onClick={issue}>
              Get code
            </button>
          </div>
          {issued && (
            <div className="card card-tight center">
              <p className="small muted">Code for {issued.name}</p>
              <p className="mono" style={{ fontSize: "2rem", letterSpacing: "0.3em" }}>
                {issued.code}
              </p>
              <p className="tiny muted">
                They open the room link, choose &quot;I have a rejoin code&quot; and enter it.
              </p>
            </div>
          )}
          {error && <p className="error-text">{error}</p>}
        </div>
      )}

      {host && playing && (
        <div className="stack-sm">
          <span className="eyebrow">Host: end the game</span>
          <p className="small muted">
            Stop this game now. Nobody wins, everyone sees the Grimoire, and you can start another.
          </p>
          <button className="btn btn-danger" onClick={endGame} disabled={busy}>
            End the game
          </button>
        </div>
      )}

      {view.phase === "lobby" && (
        <button className="btn btn-danger" onClick={leave} disabled={busy}>
          Leave room
        </button>
      )}

      <p className="tiny faint center">
        TowerClock {APP_VERSION} ·{" "}
        <a href="/changelog" target="_blank" rel="noopener">
          Changelog
        </a>
      </p>
    </Sheet>
  );
}
